import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { Card } from "components/common/Card";
import { Badge } from "components/common/Badge";
import { SegmentedControl } from "components/common/SegmentedControl";
import { Button } from "components/common/Button";
import { SeatStepper } from "components/main/organizations/SeatStepper";
import { plural } from "components/common/ImpactRow";
import type { CatalogPlan } from "@/lib/api/organizations";
import { formatBytes, formatMoneyCents } from "@/lib/format";
import type { BillingInterval } from "@/lib/paddle";

/// The published pricing page, which carries the full feature table.
export const PricingPageLink: React.FC = () => (
  <a
    href="https://mycorr.app/pricing/"
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1 text-fg-muted-regular-small hover:text-accent hover:underline"
  >
    See all features
    <OpenInNewOutlinedIcon className="icon-xs" />
  </a>
);

const Grant: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="flex items-start gap-1 text-fg-muted-regular-small">
    <CheckIcon className="icon-sm mt-0.5 shrink-0 text-fg-muted-medium" />
    {children}
  </li>
);

/// A per-seat tier's figures are the one-seat baseline, so "per seat" is on
/// each line.
export const PlanCard: React.FC<{
  tier: CatalogPlan;
  /** Paddle's localized total for the seat count chosen. */
  livePrice?: string;
  interval?: BillingInterval;
  /** Absent for a tier sold on one term only. */
  onIntervalChange?: (interval: BillingInterval) => void;
  seats: number;
  /** Seats already occupied — the floor. */
  minSeats?: number;
  state: "current" | "upgrade";
  /** Absent where the seat count is not the buyer's to set. */
  onSeatsChange?: (seats: number) => void;
  onSubscribe?: () => void;
  busy?: boolean;
}> = ({
  tier,
  livePrice,
  interval = "month",
  onIntervalChange,
  seats,
  minSeats,
  state,
  onSeatsChange,
  onSubscribe,
  busy,
}) => {
  const annual = interval === "year" ? tier.price_cents_yearly : null;
  const rate = annual ?? tier.price_cents;
  const period = annual === null ? tier.billing_period : "yearly";
  const perSeat = tier.limits.seats === 1 && tier.price_cents > 0;
  const each = perSeat ? " per seat" : "";

  const grants: string[] = [];
  if (tier.limits.storage_bytes !== null) {
    grants.push(`${formatBytes(tier.limits.storage_bytes)} storage${each}`);
  }
  if (tier.limits.egress_bytes_per_month !== null) {
    grants.push(
      `${formatBytes(tier.limits.egress_bytes_per_month)} egress a month${each}`,
    );
  }
  if (tier.limits.mcp_queries_per_month !== null) {
    grants.push(
      `${tier.limits.mcp_queries_per_month.toLocaleString()} MCP tool calls a month${each}`,
    );
  }
  // Uncapped by design and published as "Unlimited", so stated as a grant.
  if (tier.limits.max_models === null && tier.limits.max_tables === null) {
    grants.push("Unlimited models and tables");
  }
  if (tier.limits.seats !== null && !perSeat) {
    grants.push(
      `${tier.limits.seats.toLocaleString()} ${plural(tier.limits.seats, "seat")}`,
    );
  }

  const headline =
    tier.price_cents === 0
      ? formatMoneyCents(0, tier.currency)
      : (livePrice ?? formatMoneyCents(rate, tier.currency));
  // The per-seat rate is stated only when the headline is a multi-seat total.
  const basis =
    tier.price_cents === 0
      ? "per month"
      : perSeat && livePrice && seats > 1
        ? `${formatMoneyCents(rate, tier.currency)} /seat · ${period}`
        : perSeat
          ? `/seat · ${period}`
          : period;

  const buyable = state === "upgrade" && perSeat && onSeatsChange;
  // Only a card with an act on it reacts to hover.
  const choosable =
    state === "upgrade" && tier.is_purchasable && Boolean(onSubscribe);

  return (
    <Card
      variant={state === "current" ? "accent" : "default"}
      interactive={choosable}
      fill
    >
      <div className="flex flex-1 flex-col gap-3">
        <span className="flex flex-col">
          <span className="flex items-center gap-2">
            <span className="text-fg-medium">{tier.name}</span>
            {state === "current" && <Badge label="Your plan" variant="info" />}
          </span>
          {/* Two lines' room, so cards in a row align below the tagline. */}
          <span className="min-h-8 text-fg-muted-regular-small">
            {tier.description}
          </span>
        </span>

        {/* The interval switch and the stepper hold their lines where they do
            not apply, so cards in a row do not stagger. */}
        <span className="flex flex-col gap-2">
          <span className={onIntervalChange ? "h-6" : "hidden h-6 sm:block"}>
            {onIntervalChange && (
              <SegmentedControl
                options={[
                  { value: "month" as BillingInterval, label: "Monthly" },
                  { value: "year" as BillingInterval, label: "Yearly" },
                ]}
                value={interval}
                onChange={onIntervalChange}
              />
            )}
          </span>
          <span className="flex flex-col">
            <span className="font-mono text-fg-medium">{headline}</span>
            <span className="font-mono text-fg-muted-regular-small">
              {basis}
            </span>
          </span>
          {buyable ? (
            <span className="flex items-center gap-2 text-fg-muted-regular-small">
              <label htmlFor={`seats-${tier.code}`}>Seats</label>
              <SeatStepper
                id={`seats-${tier.code}`}
                seats={seats}
                min={minSeats}
                onChange={onSeatsChange}
              />
            </span>
          ) : (
            <span className="hidden h-6 sm:block" />
          )}
        </span>

        <div>
          {state === "current" ? (
            // Not a disabled Button, which is what "Not available yet" is.
            <span
              className="flex h-6 items-center text-fg-muted-regular-small"
              data-testid="plan-included"
            >
              Included with this organization
            </span>
          ) : !tier.is_purchasable ? (
            <Button variant="secondary" size="sm" disabled>
              Not available yet
            </Button>
          ) : (
            onSubscribe && (
              <Button
                variant="primary"
                size="sm"
                onClick={onSubscribe}
                loading={busy}
              >
                {`Upgrade to ${tier.name}`}
              </Button>
            )
          )}
        </div>

        {/* `flex-1` takes the row's slack. */}
        <ul className="flex flex-1 flex-col gap-1">
          {state === "upgrade" && <Grant>Everything in Free</Grant>}
          {grants.map((grant) => (
            <Grant key={grant}>{grant}</Grant>
          ))}
        </ul>
      </div>
    </Card>
  );
};

/// Not a `plans` row, so nothing about it can reach checkout.
export const EnterpriseCard: React.FC<{
  /** The top paid tier's name, for the inheritance line. */
  above?: string;
  /** Opens the support panel, aligned with the trigger's viewport top. */
  onContactSales: (anchorTop?: number) => void;
}> = ({ above, onContactSales }) => (
  <Card interactive fill>
    <div className="flex flex-1 flex-col gap-3">
      <span className="flex flex-col">
        <span className="text-fg-medium">Enterprise</span>
        <span className="min-h-8 text-fg-muted-regular-small">
          For an organization that needs its own cloud, its own agreement, or
          limits nobody publishes.
        </span>
      </span>

      <span className="flex flex-col gap-2">
        <span className="h-6" />
        <span className="flex flex-col">
          <span className="font-mono text-fg-medium">Custom</span>
          <span className="font-mono text-fg-muted-regular-small">
            on request
          </span>
        </span>
        <span className="h-6" />
      </span>

      <div>
        <Button
          variant="secondary"
          size="sm"
          onClick={(e) =>
            onContactSales(e.currentTarget.getBoundingClientRect().top)
          }
          data-testid="contact-sales"
        >
          Contact sales
        </Button>
      </div>

      <ul className="flex flex-1 flex-col gap-1">
        {above && <Grant>{`Everything in ${above}`}</Grant>}
        <Grant>Your own cloud, or on-prem</Grant>
        <Grant>SSO, SAML and audit logs</Grant>
        <Grant>Custom limits, dedicated support and a DPA</Grant>
      </ul>
    </div>
  </Card>
);
